import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import { BreadcrumbItem } from '../components/Header';
import { progressService } from '../services/progressService';
import { isAuthenticationError } from '../utils/authError';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';

interface Step5Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step5: React.FC<Step5Props> = ({ user, onLogin, onLogout }) => {
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Five",
      description: "Discover why revealing the exact nature of my wrongs brings release and clarity",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Five (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Five",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Take Step Five: Share my Step Four inventories with my sponsor",
      description: "Share your completed inventories with your sponsor",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);

  // State for action 3 fields
  const [inventoryShared, setInventoryShared] = useState(false);
  const [characterDefects, setCharacterDefects] = useState<string[]>([]);
  const [savingList, setSavingList] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  
  // Auto-save state management
  const [savingDefects, setSavingDefects] = useState(false);
  const [savedDefects, setSavedDefects] = useState(false);
  const [saveDefectsError, setSaveDefectsError] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Five', path: '/saa/12-steps/step-5', active: true }
  ];

  const handleGreenBookClick = () => {
    setIsContentReaderOpen(true);
  };

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 5).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          // Merge saved actions with original actions to restore missing properties
          const mergedActions = originalActions.map(originalAction => {
            const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
            if (savedAction) {
              return {
                ...originalAction,
                completed: savedAction.completed,
                description: savedAction.description || originalAction.description
              };
            }
            return originalAction;
          });
          setActions(mergedActions);
        }
        
        // Restore character defects
        if (savedData.characterDefects) {
          setCharacterDefects(savedData.characterDefects);
        }
        
        // Restore inventoryShared checkbox state
        if (savedData.inventoryShared !== undefined) {
          setInventoryShared(savedData.inventoryShared);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      // Check if this is an authentication error
      if (isAuthenticationError(err)) {
        setAuthError(true);
        setError('Your session has expired. Please log in again to continue.');
      } else {
        setError('Failed to load your progress. Please try refreshing the page.');
      }
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    // All previous actions must be completed
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    const newCollapsed = new Set(collapsedActions);
    if (newCollapsed.has(actionId)) {
      newCollapsed.delete(actionId);
    } else {
      newCollapsed.add(actionId);
    }
    setCollapsedActions(newCollapsed);
  };

  // Handle character defect input changes
  const handleCharacterDefectChange = (index: number, value: string) => {
    const newDefects = [...characterDefects];
    
    // Ensure array is large enough
    while (newDefects.length <= index) {
      newDefects.push('');
    }
    
    newDefects[index] = value;
    
    // Remove trailing empty strings to keep array clean
    while (newDefects.length > 0 && newDefects[newDefects.length - 1] === '') {
      newDefects.pop();
    }
    
    setCharacterDefects(newDefects);
  };

  // Handle Enter key press to move to next field
  const handleCharacterDefectKeyPress = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Find the next input field
      const nextIndex = index + 1;
      const nextInput = document.querySelector(`input[data-defect-index="${nextIndex}"]`) as HTMLInputElement;
      
      if (nextInput) {
        nextInput.focus();
      } else if (nextIndex < 9) {
        // If next input doesn't exist but we're under the limit, create it and focus
        const newDefects = [...characterDefects];
        while (newDefects.length <= nextIndex) {
          newDefects.push('');
        }
        setCharacterDefects(newDefects);
        
        // Focus the new input after a brief delay to ensure it's rendered
        setTimeout(() => {
          const newInput = document.querySelector(`input[data-defect-index="${nextIndex}"]`) as HTMLInputElement;
          if (newInput) {
            newInput.focus();
          }
        }, 10);
      }
    }
  };

  // Validate action 3 completion
  const validateAction3Completion = () => {
    return inventoryShared && characterDefects.some(defect => defect.trim().length > 0);
  };
  
  // Check if action 3 can be completed (used by StepActions component)
  const canCompleteAction3 = (actionId: number): boolean => {
    if (actionId !== 3) return true; // Other actions can always be completed
    return validateAction3Completion(); // Action 3 requires checkbox + at least 1 defect
  };

  // Auto-save character defects with debouncing
  const autoSaveDefects = async (newInventoryShared?: boolean) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      if (!user) return;

      // Set saving state
      setSavingDefects(true);
      setSaveDefectsError(false);
      setSavedDefects(false);

      try {
        // Filter out empty defects
        const nonEmptyDefects = characterDefects.filter(defect => defect.trim().length > 0);
        
        // Use the new value if provided, otherwise use current state
        const inventorySharedValue = newInventoryShared !== undefined ? newInventoryShared : inventoryShared;
        
        // Save to API (saves empty array if no defects, which is fine for checkpoint)
        await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 5,
          step_title: 'Step Five',
          completed: actions.every(a => a.completed),
          data: {
            actions,
            inventoryShared: inventorySharedValue,
            characterDefects: nonEmptyDefects
          }
        });

        // Show success indicator
        setSavingDefects(false);
        setSavedDefects(true);
        setTimeout(() => setSavedDefects(false), 2000);

      } catch (err) {
        console.error('Error auto-saving character defects:', err);
        setSavingDefects(false);
        setSaveDefectsError(true);
        setTimeout(() => setSaveDefectsError(false), 3000);
      }
    }, 300);
  };

  // Save character defects list
  const saveCharacterDefectsList = async () => {
    if (!user) {
      setError('User not logged in. Please log in to save progress.');
      return;
    }

    // Filter out empty defects
    const nonEmptyDefects = characterDefects.filter(defect => defect.trim().length > 0);
    
    if (nonEmptyDefects.length === 0) {
      setError('Please enter at least one character defect before saving.');
      return;
    }

    setSavingList(true);
    try {
      // Save to backend
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 5,
        step_title: 'Step Five',
        completed: false,
        data: {
          characterDefects: nonEmptyDefects
        }
      });

      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
      
    } catch (err) {
      console.error('Error saving character defects list:', err);
      setError('Failed to save character defects list. Please try again.');
    } finally {
      setSavingList(false);
    }
  };

  // Custom render function for action 3
  const renderAction3Content = (action: ActionItem, index: number) => {
    if (action.id !== 3) return null;

    return (
      <div style={{ marginBottom: 16, width: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              id="inventory-shared"
              checked={inventoryShared}
              onChange={async (e) => {
                const newValue = e.target.checked;
                setInventoryShared(newValue);
                const action3 = actions.find(a => a.id === 3);
                const wasCompleted = action3?.completed;
                // If unchecking "Shared my inventories" and action 3 is completed, uncomplete it
                if (!newValue && wasCompleted) {
                  const updatedActions = actions.map(a =>
                    a.id === 3 ? { ...a, completed: false } : a
                  );
                  setActions(updatedActions);
                  if (user) {
                    try {
                      const allCompleted = updatedActions.every(a => a.completed);
                      const updatedStepData = await progressService.updateStepProgress({
                        program: 'SAA',
                        track: '12-steps',
                        step_number: 5,
                        step_title: 'Step Five',
                        completed: allCompleted,
                        data: {
                          actions: updatedActions,
                          inventoryShared: false,
                          characterDefects
                        }
                      });
                      if (updatedStepData?.step) setStepData(updatedStepData.step);
                      const completedCount = updatedActions.filter(a => a.completed).length;
                      await progressService.updateStatus({
                        program: 'SAA',
                        track: '12-steps',
                        status_note: `Working Step Five - ${completedCount} of 3 actions completed`,
                        progress_milestone: allCompleted ? 'step5_completed' : 'step5_in_progress'
                      });
                    } catch (err: any) {
                      if (isAuthenticationError(err)) {
                        setAuthError(true);
                        setError('Your session has expired. Please log in again to save your progress.');
                      } else {
                        setError('Failed to save. Please try again.');
                      }
                      setInventoryShared(true);
                      setActions(actions);
                    }
                  }
                } else {
                  autoSaveDefects(newValue);
                }
              }}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontWeight: 500, color: '#333' }}>
              Shared my inventories with my sponsor / trusted program friend
            </span>
          </label>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <label style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>
              Identified character defects (shortcoming, flaws)
            </label>
            <button
              onClick={() => setIsExamplesOpen(true)}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Character defect examples
            </button>
          </div>
          <div style={{ marginBottom: 16 }}>
            {Array.from({ length: Math.max(characterDefects.length + 1, 1) }, (_, idx) => {
              // Show existing defects plus one empty input, but max 9 total
              if (idx >= 9) return null;
              
              // Determine placeholder text
              let placeholder = "";
              if (characterDefects.length === 0 && idx === 0) {
                placeholder = "Enter a character defect...";
              } else if (idx === characterDefects.length) {
                placeholder = "Add another character defect...";
              }
              
              return (
                <input
                  key={idx}
                  type="text"
                  value={characterDefects[idx] || ''}
                  onChange={(e) => handleCharacterDefectChange(idx, e.target.value)}
                  onKeyPress={(e) => handleCharacterDefectKeyPress(idx, e as React.KeyboardEvent)}
                  onBlur={() => autoSaveDefects()}
                  data-defect-index={idx}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                  placeholder={placeholder}
                />
              );
            })}
          </div>
          
          {/* Auto-save indicator */}
          <div style={{ 
            textAlign: 'right', 
            minHeight: 24,
            fontSize: '14px',
            fontWeight: 500
          }}>
            {savingDefects && (
              <span style={{ color: '#6c757d' }}>
                Saving...
              </span>
            )}
            {savedDefects && (
              <span style={{ color: '#28a745' }}>
                ✓ All changes saved
              </span>
            )}
            {saveDefectsError && (
              <span style={{ color: '#dc3545' }}>
                ⚠ Save failed. Please try again.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle action changes and save to backend
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    // If trying to complete action 3, show validation message if requirements not met
    // Note: canCompleteAction prevents the checkbox from being checked, but we keep this
    // as a backup check and to show the validation message
    if (action3 && !currentAction3?.completed && action3.completed) {
      if (!validateAction3Completion()) {
        setValidationMessage("To complete this action, confirm that you've shared your Step Four inventories with your sponsor and saved at least one character defect");
        setShowValidationPopup(true);
        // Revert the action state - don't mark as complete
        return;
      }
      
      // Character defects are entered - auto-save and mark complete
      if (!user) {
        setError('User not logged in. Please log in.');
        return;
      }
      
      try {
        // Save to backend
        const updatedStepData = await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 5,
          step_title: 'Step Five',
          completed: updatedActions.every(a => a.completed),
          data: {
            actions: updatedActions,
            characterDefects,
            inventoryShared
          }
        });
        
        // Update step data for history modal
        if (updatedStepData && updatedStepData.step) {
          setStepData(updatedStepData.step);
        }
        
        // Update local state
        setActions(updatedActions);
      } catch (err: any) {
        // Check if this is an authentication error
        if (isAuthenticationError(err)) {
          setAuthError(true);
          setError('Your session has expired. Please log in again to save your progress.');
        } else {
          setError('Failed to save character defects. Please try again.');
        }
        return; // Don't update actions on error
      }
    } else {
      // For other actions or unchecking action 3, save to backend
      if (!user) {
        setError('User not logged in. Please log in.');
        return;
      }
      
      try {
        // Update local state
        setActions(updatedActions);
        
        // Save to backend
        const allCompleted = updatedActions.every(action => action.completed);
        const updatedStepData = await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 5,
          step_title: 'Step Five',
          completed: allCompleted,
          data: {
            actions: updatedActions,
            characterDefects,
            inventoryShared
          }
        });
        
        // Update step data for history modal
        if (updatedStepData && updatedStepData.step) {
          setStepData(updatedStepData.step);
        }
        
        // Update status note
        const completedCount = updatedActions.filter(a => a.completed).length;
        await progressService.updateStatus({
          program: 'SAA',
          track: '12-steps',
          status_note: `Working Step Five - ${completedCount} of 3 actions completed`,
          progress_milestone: allCompleted ? 'step5_completed' : 'step5_in_progress'
        });
      } catch (err: any) {
        console.error('Error updating action progress:', err);
        // Check if this is an authentication error
        if (isAuthenticationError(err)) {
          setAuthError(true);
          setError('Your session has expired. Please log in again to save your progress.');
        } else {
          setError('Failed to save progress. Please try again.');
        }
      }
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Five</h1>
            {stepData && (
              <HistoryButton
                stepNumber={5}
                stepTitle="Step Five"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            "Admitted to God, to ourselves, and to another human being the exact nature of our wrongs."
          </p>
          

          

        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            {authError ? (
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button 
                  onClick={onLogin} 
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Log In Again
                </button>
                <button 
                  onClick={() => window.location.reload()} 
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Refresh Page
                </button>
              </div>
            ) : (
              <button onClick={() => window.location.reload()}>Refresh Page</button>
            )}
          </div>
        )}

        {!isInitializing && !error && (
          <>
            {!user && (
              <StepLoginPrompt onLogin={onLogin} />
            )}

        {user && (
          <StepActions
            actions={actions}
            onActionChange={handleActionChange}
            renderActionContent={renderAction3Content}
            stepNumber={5}
            canCompleteAction={canCompleteAction3}
          />
        )}

        {allCompleted && (
          <>
            <div className={styles.completionMessage}>
              <h3>🎉 Congratulations!</h3>
              <p>You have completed all actions for Step Five and can move on to Step Six.</p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-4" className={styles.prevStepLink}>
                  {'<<'} Step Four
                </a>
                <span></span>
                <a href="/saa/12-steps/step-6" className={styles.nextStepLink}>
                  Step Six {'>>'}
                </a>
              </div>
            </div>
          </>
        )}

        {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-4" className={styles.prevStepLink}>
                {'<<'} Step Four
              </a>
              <span></span>
              <a href="/saa/12-steps/step-6" className={styles.nextStepLink}>
                Step Six {'>>'}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>
      
      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-five start>>"
        endTag="<<step-five end>>"
      />

      {/* ContentReader for How to Work Step Five */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_5_Guidance.md"
        startTag="<<working-step-five start>>"
        endTag="<<working-step-five end>>"
        title="Guidance on working Step Five"
      />

      {/* ContentReader for Examples */}
      <ContentReader
        isOpen={isExamplesOpen}
        onClose={() => setIsExamplesOpen(false)}
        filePath="/content/character_defects_step6.md"
        startTag=""
        endTag=""
        title="Example character defects / shortcomings"
      />

      {/* Validation Modal */}
      {showValidationPopup && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: 24, 
            borderRadius: 8, 
            maxWidth: 400, 
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Action Not Complete</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: 1.4 }}>
              {validationMessage}
            </p>
            <button
              onClick={() => setShowValidationPopup(false)}
              style={{ 
                background: '#007bff', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 24px', 
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="IRYVP5A6XmM"
        title="Step Five Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={5}
        stepTitle="Step Five"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />
    </div>
  );
};

export default Step5; 
